/**
 * useScheduleEditor — all state and handlers for the schedule editor.
 *
 * Accepts a discriminated-union input so callers never pass ambiguous booleans.
 * The hook resolves baseSchedule and baseScheduleId by narrowing on `mode` —
 * no intermediate `isNew` boolean is created, preserving TypeScript's narrowing.
 */
import { useState, useCallback } from "react";
import {
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { EditableRetreatSchedule } from "@retreat/core";
import type { BellPatternId } from "@retreat/core";
import type { ContainerTypeMeta } from "@retreats/content";
import { CONTAINER_DEFAULTS } from "@retreats/content";
import type { UserSchedule } from "../state/userScheduleRepository";
import type { EditorContainer } from "../components/ContainerEditorSheet";

// ── Constants ─────────────────────────────────────────────────────────────────

const FALLBACK_CONTAINER_DEFAULTS = {
  durationSeconds: 1800,
  hasBellBefore: true,
  hasBellAfter: false,
};
const POINTER_ACTIVATION_DISTANCE_PX = 8;
const TOUCH_ACTIVATION_DELAY_MS = 250;
const TOUCH_ACTIVATION_TOLERANCE_PX = 5;

const BLANK_SCHEDULE: EditableRetreatSchedule = {
  id: "",
  name: "",
  version: "2.0.0",
  days: [{ dayNumber: 1, name: "Day 1", containers: [] }],
};

// ── Types ─────────────────────────────────────────────────────────────────────

export type UseScheduleEditorInput =
  | {
      mode: "new";
      bellPatterns: BellPatternId[];
      containerTypes: ContainerTypeMeta[];
      onSave: (e: UserSchedule) => void;
    }
  | {
      mode: "fork";
      bellPatterns: BellPatternId[];
      schedule: EditableRetreatSchedule;
      presetId: string;
      containerTypes: ContainerTypeMeta[];
      onSave: (e: UserSchedule) => void;
    }
  | {
      mode: "edit";
      bellPatterns: BellPatternId[];
      schedule: EditableRetreatSchedule;
      baseScheduleId: string | null;
      containerTypes: ContainerTypeMeta[];
      onSave: (e: UserSchedule) => void;
    };

export type ScheduleEditorHandle = {
  name: string;
  setName: (n: string) => void;
  containers: EditorContainer[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  showAddPicker: boolean;
  setShowAddPicker: (show: boolean) => void;
  sensors: ReturnType<typeof useSensors>;
  handleDragEnd: (event: DragEndEvent) => void;
  updateContainer: (c: EditorContainer) => void;
  deleteContainer: (id: string) => void;
  addContainer: (type: ContainerTypeMeta) => void;
  handleSave: () => void;
  editingContainer: EditorContainer | null;
  dotForType: (type: string) => string;
  title: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function flatten(schedule: EditableRetreatSchedule): EditorContainer[] {
  return schedule.days.flatMap((d) =>
    d.containers.map((c) => ({
      id: c.id,
      type: c.type,
      name: c.name,
      durationSeconds: c.durationSeconds,
      bellBeforePattern:
        (c as { bellBeforePattern?: BellPatternId }).bellBeforePattern ?? null,
      bellAfterPattern:
        (c as { bellAfterPattern?: BellPatternId }).bellAfterPattern ?? null,
    })),
  );
}

// Each container type has its own required field(s) beyond the common shape
// (name/duration/bells) — ContainerSchema is a discriminated union, and a container
// missing its type's required field fails validation, taking the *entire* schedule
// down with it silently at load time. Only meditation needs an explicit fallback for
// its required, defaultless fields (posture, bellsBefore, bellsAfter, optionalKinhin);
// every other type's own required field gets a sensible placeholder, and the rest
// (bellsBefore/bellsAfter) fall back to that type's zod .default() on save.
function defaultFieldsForType(
  type: string,
  name: string,
): Record<string, unknown> {
  switch (type) {
    case "meditation":
      return {
        posture: "seated",
        bellsBefore: 1,
        bellsAfter: 1,
        optionalKinhin: false,
      };
    case "meal":
      return { mealType: "lunch", mindfulPractice: true };
    case "work":
      return { workType: name };
    case "physical_practice":
      return { practiceType: name };
    case "teaching":
      return { teachingTopic: name };
    default:
      return {};
  }
}

function rebuild(
  base: EditableRetreatSchedule,
  containers: EditorContainer[],
  newId: string,
  newName: string,
): EditableRetreatSchedule {
  const rebuilt = containers.map((ec) => {
    const original = base.days
      .flatMap((d) => d.containers)
      .find((c) => c.id === ec.id);
    // Only reuse the original container's fields if its type hasn't changed — otherwise
    // stale fields from the old type would linger and the new type's own required
    // field would still be missing.
    const baseContainer =
      original && original.type === ec.type
        ? original
        : {
            id: ec.id,
            type: ec.type,
            name: ec.name,
            durationSeconds: ec.durationSeconds,
            ...defaultFieldsForType(ec.type, ec.name),
          };
    return {
      ...baseContainer,
      name: ec.name,
      type: ec.type as typeof baseContainer.type,
      durationSeconds: ec.durationSeconds,
      bellBeforePattern: ec.bellBeforePattern ?? undefined,
      bellAfterPattern: ec.bellAfterPattern ?? undefined,
    };
  });
  return {
    ...base,
    id: newId,
    name: newName,
    days: [
      {
        dayNumber: 1,
        name: "Day 1",
        containers: rebuilt as EditableRetreatSchedule["days"][0]["containers"],
      },
    ],
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useScheduleEditor(
  input: UseScheduleEditorInput,
): ScheduleEditorHandle {
  const baseSchedule = input.mode === "new" ? BLANK_SCHEDULE : input.schedule;
  const { containerTypes, onSave } = input;

  const [name, setName] = useState(baseSchedule.name);
  const [containers, setContainers] = useState<EditorContainer[]>(() =>
    flatten(baseSchedule),
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddPicker, setShowAddPicker] = useState(false);
  // "edit" always updates the already-owned schedule in place; "new"/"fork" always mint a fresh id,
  // otherwise re-editing a forked schedule would silently create a duplicate instead of updating it.
  const [scheduleId] = useState(() =>
    input.mode === "edit" ? baseSchedule.id : crypto.randomUUID(),
  );
  // Lineage persisted on the saved UserSchedule: null from scratch, the preset id on first fork,
  // and preserved unchanged on every later edit of an already-owned schedule.
  const [baseScheduleId] = useState(() => {
    if (input.mode === "new") return null;
    if (input.mode === "fork") return input.presetId;
    return input.baseScheduleId;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: POINTER_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_ACTIVATION_DELAY_MS,
        tolerance: TOUCH_ACTIVATION_TOLERANCE_PX,
      },
    }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setContainers((prev) => {
        const oldIdx = prev.findIndex((c) => c.id === active.id);
        const newIdx = prev.findIndex((c) => c.id === over.id);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  }, []);

  function updateContainer(updated: EditorContainer) {
    setContainers((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c)),
    );
  }

  function deleteContainer(id: string) {
    setContainers((prev) => prev.filter((c) => c.id !== id));
  }

  function addContainer(type: ContainerTypeMeta) {
    const defaults =
      CONTAINER_DEFAULTS[type.type] ?? FALLBACK_CONTAINER_DEFAULTS;
    const firstPattern = input.bellPatterns[0] ?? null;
    const newC: EditorContainer = {
      id: crypto.randomUUID(),
      type: type.type,
      name: type.label,
      durationSeconds: defaults.durationSeconds,
      bellBeforePattern: defaults.hasBellBefore ? firstPattern : null,
      bellAfterPattern: defaults.hasBellAfter ? firstPattern : null,
    };
    setContainers((prev) => [...prev, newC]);
    setShowAddPicker(false);
    setEditingId(newC.id);
  }

  function handleSave() {
    const built = rebuild(
      baseSchedule,
      containers,
      scheduleId,
      name.trim() || baseSchedule.name,
    );
    const now = Date.now();
    onSave({
      schedule: built,
      baseScheduleId,
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
    });
  }

  const editingContainer = containers.find((c) => c.id === editingId) ?? null;
  const dotForType = (type: string) =>
    containerTypes.find((ct) => ct.type === type)?.dotToken ??
    "var(--rc-ink-3)";

  return {
    name,
    setName,
    containers,
    editingId,
    setEditingId,
    showAddPicker,
    setShowAddPicker,
    sensors,
    handleDragEnd,
    updateContainer,
    deleteContainer,
    addContainer,
    handleSave,
    editingContainer,
    dotForType,
    title: input.mode === "new" ? "New schedule" : "Edit schedule",
  };
}
